import { Button } from '@eventbrite/eds-button';
import { Icon } from '@eventbrite/eds-icon';
import { Calendar } from '@eventbrite/eds-iconography';
import { getLayer, isStatsigInitialized } from '@eventbrite/statsig';
import React from 'react';
import { LISTING_PAGE_ORGANIZER_PANEL_EXPERIMENT } from '../../../../constants';
import type {
    CheckoutWidgetProps,
    Event,
    Organizer,
    RemindMeProps,
} from '../../../../types/index';
import RemindMeSsr from '../../../RemindMeSsr/RemindMeSsr';
import { RSVPLabel, RSVPOptions } from '../../../RSVP/RSVP';
import { CheckoutModalButton } from '../CheckoutModalButton/CheckoutModalButton';
import './ConversionBar.scss';

export type ConversionBarCommonProps = {
    statusIsUnavailable: boolean;
    shouldDisplayRsvp: boolean;
    shouldShowStatus: boolean;
    panelDisplayPrice: string;
    statusToDisplay: string;
    shouldShowCheckoutButton: boolean;
    getSeriesDefaultOptionTerminology: string;
    ticketModalButton: any;
    shouldShowRemindMeButton: boolean;
    shouldShowTurnOffReminderButton: boolean;
    isExpiredEvent: boolean;
};

export type ConversionBarComponentProps = ConversionBarCommonProps &
    CheckoutWidgetProps &
    RemindMeProps & {
        externalTickets?: {
            //TODO: build on the backend
            url: string;
            text: string;
            available: boolean;
            shouldShowExternalTicketingProvider: boolean;
            externalTicketingProviderLabelText?: string;
        };
    } & Pick<Event, 'isSeriesParent' | 'isPreview'> & {
        eventId: Event['id'];
        eventName?: Event['name'];
        organizerName?: Organizer['name'];
        organizerAvatar?: Organizer['profilePicture'];
        organizerUrl?: Organizer['name'];
    };

export const ConversionBar = ({
    affiliateCode,
    campaign_token,
    promoCode,
    rsvpToken,
    waitlistToken,
    statusIsUnavailable,
    shouldDisplayRsvp,
    shouldShowStatus,
    statusToDisplay,
    panelDisplayPrice,
    externalTickets,
    ticketModalButton,
    eventId,
    isPreview,
    shouldShowRemindMeButton,
    shouldShowCheckoutButton,
    isSeriesParent,
    getSeriesDefaultOptionTerminology,
    eventName,
    eventUrl,
    isRemindedEvent,
    organizerId,
    organizerName,
    organizerAvatar,
    organizerUrl,
    isAuthenticated,
}: ConversionBarComponentProps) => {
    const {
        shouldShowExternalTicketingProvider,
        externalTicketingProviderLabelText,
    } = externalTickets ?? {};

    if (!isStatsigInitialized()) {
        return null;
    }

    const organizerPanelExperimentValue = getLayer(
        LISTING_PAGE_ORGANIZER_PANEL_EXPERIMENT.layer,
        LISTING_PAGE_ORGANIZER_PANEL_EXPERIMENT.param,
        LISTING_PAGE_ORGANIZER_PANEL_EXPERIMENT.defaultValue,
    );

    const isOldOrganizerPanelControlVariant =
        organizerPanelExperimentValue ===
        LISTING_PAGE_ORGANIZER_PANEL_EXPERIMENT.defaultValue;

    const ExternalTickets = () => (
        <Button
            href={externalTickets?.url}
            type="link"
            style="fill"
            size="block"
            target="_blank"
            rel="noreferrer"
        >
            {externalTickets?.text}
        </Button>
    );

    const CheckoutButton: React.FC = ({ children }) => (
        <CheckoutModalButton
            {...{
                affiliateCode,
                campaign_token,
                children,
                eventId,
                isPreview,
                isSeriesParent,
                promoCode,
                rsvpToken,
                shouldShowStatus,
                ticketModalButton,
                waitlistToken,
            }}
        />
    );

    const RemindMeButton: React.FC = () => (
        <>
            {isOldOrganizerPanelControlVariant ? (
                <div id="listings-root__remind-me"></div>
            ) : (
                <RemindMeSsr
                    eventId={eventId}
                    eventUrl={eventUrl}
                    eventName={eventName}
                    isRemindedEvent={isRemindedEvent}
                    organizerId={organizerId}
                    organizerName={organizerName}
                    organizerAvatar={organizerAvatar}
                    organizerUrl={organizerUrl}
                    isAuthenticated={isAuthenticated}
                />
            )}
        </>
    );

    const CheckoutArea = () => (
        <>
            {shouldShowRemindMeButton && <RemindMeButton />}
            <CheckoutButton />
        </>
    );

    const SingleEvent = () => (
        <>
            {shouldDisplayRsvp ? (
                <RSVPOptions
                    {...{
                        affiliateCode,
                        campaign_token,
                        eventId,
                        isPreview,
                        isSeriesParent,
                        promoCode,
                        rsvpToken,
                        shouldShowStatus,
                        ticketModalButton,
                        waitlistToken,
                    }}
                />
            ) : (
                <>
                    {shouldShowCheckoutButton && <CheckoutArea />}
                    {externalTickets?.available && <ExternalTickets />}
                </>
            )}
        </>
    );

    const SeriesParent = () => (
        <>
            {shouldShowStatus ? (
                <CheckoutButton />
            ) : (
                <CheckoutButton>
                    <Icon type={<Calendar />} />
                    {getSeriesDefaultOptionTerminology}
                </CheckoutButton>
            )}
        </>
    );

    const PanelInfo = () => {
        const status = shouldShowStatus ? statusToDisplay : panelDisplayPrice;

        return (
            <div
                className="conversion-bar__panel-info"
                data-testid="panel-info"
            >
                {shouldDisplayRsvp ? (
                    <div id="listings-root__rsvp-label">
                        <RSVPLabel />
                    </div>
                ) : (
                    <>
                        {status}
                        {shouldShowExternalTicketingProvider && (
                            <>
                                {' '}
                                <span className="conversion-bar__additional-info">
                                    {externalTicketingProviderLabelText}
                                </span>
                            </>
                        )}
                    </>
                )}
            </div>
        );
    };

    const shouldShowCTA =
        !statusIsUnavailable &&
        (shouldShowCheckoutButton ||
            externalTickets?.available ||
            shouldDisplayRsvp);

    return (
        <div
            className="conversion-bar conversion-bar--checkout-opener eds-fx--slide-up"
            data-testid="conversion-bar"
        >
            <div className="conversion-bar__body">
                <PanelInfo />
            </div>
            {shouldShowCTA && (
                <div className="conversion-bar__cta-wrapper eds-fx--fade-in eds-fx--delay-18">
                    {isSeriesParent ? <SeriesParent /> : <SingleEvent />}
                </div>
            )}
        </div>
    );
};
